import React, { useEffect, useMemo, useState } from 'react';
import { Avatar } from '@jotpsych/ui';
import type { SvgIconComponent } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import PaymentsIcon from '@mui/icons-material/Payments';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import VerifiedIcon from '@mui/icons-material/Verified';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { WorklistItem } from '@store/generated/eligibility';

import { ELIG_TOKENS } from '../theme/readinessTokens';
import {
  deriveWorklistRowState,
  isRunnableState,
  type WorklistRowState,
} from '../utils/worklistRowState';

import { ReadinessIndicator } from './ReadinessIndicator';

export const ELIGIBILITY_CHECKS_TUTORIAL_ID = 'eligibility-checks-worklist';
export const ELIGIBILITY_CHECKS_TUTORIAL_VERSION = 1;

export type EligibilityChecksOnboardingMode = 'invite' | 'tour';

export interface EligibilityChecksOnboardingModalProps {
  open: boolean;
  initialMode?: EligibilityChecksOnboardingMode;
  initialStep?: number;
  onStart?: () => void;
  onDismiss: () => void;
  onComplete: () => void;
}

interface TourStep {
  icon: SvgIconComponent;
  title: string;
  description: string;
  points: string[];
  cta: string;
  preview: React.ReactNode;
}

type PreviewFocus = 'overview' | 'badges' | 'verification' | 'collection' | 'batch' | 'cost';

const TOP_ALIGNED_BADGE_PIXEL_OFFSET = 4;

const STATUS_UI: Record<
  WorklistRowState,
  {
    label: string;
    color: string;
    tint: string;
    action: string;
  }
> = {
  verified_lock_active: {
    label: 'Verified',
    color: ELIG_TOKENS.green,
    tint: ELIG_TOKENS.greenTint,
    action: 'View',
  },
  collected: {
    label: 'Collected',
    color: ELIG_TOKENS.green,
    tint: ELIG_TOKENS.greenTint,
    action: 'View',
  },
  self_pay: {
    label: 'Self-pay',
    color: ELIG_TOKENS.green,
    tint: ELIG_TOKENS.greenTint,
    action: 'View',
  },
  awaiting_check: {
    label: 'Needs check',
    color: ELIG_TOKENS.gray,
    tint: ELIG_TOKENS.grayTint,
    action: 'Run check',
  },
  needs_coverage: {
    label: 'Needs coverage',
    color: ELIG_TOKENS.gray,
    tint: ELIG_TOKENS.grayTint,
    action: 'Add coverage',
  },
  attention: {
    label: 'Needs verification',
    color: ELIG_TOKENS.amber,
    tint: ELIG_TOKENS.amberTint,
    action: 'Verify',
  },
  verified_lock_expired: {
    label: 'Verification expired',
    color: ELIG_TOKENS.amber,
    tint: ELIG_TOKENS.amberTint,
    action: 'Re-verify',
  },
  collect: {
    label: 'Collect',
    color: ELIG_TOKENS.green,
    tint: ELIG_TOKENS.greenTint,
    action: 'Collect',
  },
  blocked: {
    label: 'Blocked',
    color: ELIG_TOKENS.red,
    tint: ELIG_TOKENS.redTint,
    action: 'Fix',
  },
  info: {
    label: 'Info',
    color: ELIG_TOKENS.gray,
    tint: ELIG_TOKENS.grayTint,
    action: 'View',
  },
};

const MOCK_WORKLIST_ITEMS: WorklistItem[] = [
  {
    patient_id: 'patient-001',
    patient_name: 'Maya Chen',
    next_appointment_at: '2026-05-04T09:00:00Z',
    encounter_type: 'Therapy',
    duration_minutes: 45,
    coverage_kind: 'insurance',
    payer_name: 'Aetna',
    provider_name: 'Dr. Avery Stone',
    provider_license_level: 'PsyD',
    readiness: 'collect',
    readiness_label: 'Collect $30 co-pay',
    summary: 'Coverage is active. Collect $30 co-pay.',
    action_verb: 'Collect',
    coverage_id: 'coverage-001',
    task_priority: 20,
    member_obligation_cents: null,
    patient_cost: {
      amount_status: 'undetermined',
      amount_cents: null,
      source: null,
      reason: 'benefits_incomplete',
      policy_version: 'fixed-copay-v2',
      copayment_cents: 3000,
      coverage_id: 'coverage-001',
      collection: { kind: 'fixed_copay', amount_cents: 3000 },
    },
  },
  {
    patient_id: 'patient-002',
    patient_name: 'Andre Williams',
    next_appointment_at: '2026-05-04T10:30:00Z',
    encounter_type: 'Intake',
    duration_minutes: 60,
    coverage_kind: 'insurance',
    payer_name: 'BCBS',
    provider_name: 'Lina Gomez',
    provider_license_level: 'LCSW',
    readiness: 'attention',
    readiness_label: 'Verify with payer',
    summary: 'Payer returned partial coverage details. Verify before billing.',
    action_verb: 'Review details',
    task_priority: 15,
  },
  {
    patient_id: 'patient-003',
    patient_name: 'Nora Patel',
    next_appointment_at: '2026-05-04T11:15:00Z',
    encounter_type: 'Medication management',
    duration_minutes: 30,
    coverage_kind: 'insurance',
    payer_name: 'UnitedHealthcare',
    provider_name: 'Dr. Samir Rao',
    provider_license_level: 'MD',
    readiness: 'verified',
    readiness_label: 'Verified',
    summary: 'Coverage is active for the upcoming appointment.',
    task_priority: 4,
  },
  {
    patient_id: 'patient-004',
    patient_name: 'Leo Martinez',
    next_appointment_at: '2026-05-04T01:00:00Z',
    encounter_type: 'Follow-up',
    duration_minutes: 30,
    coverage_kind: 'insurance',
    payer_name: 'Cigna',
    provider_name: 'Dr. Avery Stone',
    provider_license_level: 'PsyD',
    readiness: null,
    readiness_label: 'Awaiting check',
    summary: 'No current eligibility check for this visit.',
    action_verb: 'Run check',
    task_priority: 10,
  },
  {
    patient_id: 'patient-005',
    patient_name: 'Priya Shah',
    next_appointment_at: '2026-05-04T02:30:00Z',
    encounter_type: 'Therapy',
    duration_minutes: 45,
    coverage_kind: 'none',
    payer_name: null,
    provider_name: 'Lina Gomez',
    provider_license_level: 'LCSW',
    readiness: null,
    readiness_label: 'Needs coverage',
    summary: 'Add insurance before an eligibility check can run.',
    action_verb: 'Add coverage',
    task_priority: 12,
  },
];

export const EligibilityChecksOnboardingModal: React.FC<EligibilityChecksOnboardingModalProps> = ({
  open,
  initialMode = 'invite',
  initialStep = 0,
  onStart,
  onDismiss,
  onComplete,
}) => {
  const [mode, setMode] = useState<EligibilityChecksOnboardingMode>(initialMode);
  const [stepIndex, setStepIndex] = useState(initialStep);

  const steps = useMemo<TourStep[]>(
    () => [
      {
        icon: FactCheckIcon,
        title: "Get today's visits billing-ready",
        description:
          'Before the first appointment, see which visits are ready, which benefits need a quick payer check.',
        points: [
          'Start with the visits that could slow down a claim or collection.',
          'Spend less time chasing already-clear coverage.',
        ],
        cta: 'Next',
        preview: <FocusedPreview focus="overview" />,
      },
      {
        icon: HealthAndSafetyIcon,
        title: 'Work the exceptions first',
        description:
          'The list separates routine visits from the ones that need action: review benefit details, verify unclear coverage, or fix missing coverage before the claim is created.',
        points: [
          'Collect the reported fixed co-pay; coinsurance dollars remain undetermined.',
          'Verify: confirm coverage details by phone or payer portal.',
        ],
        cta: 'Next',
        preview: <FocusedPreview focus="badges" />,
      },
      {
        icon: VerifiedIcon,
        title: 'Save the payer verification',
        description:
          'After a payer call or portal lookup, save the reference number and the coverage details that affect billing: copay, deductible, limits, and authorization notes.',
        points: [
          'Show whether it came from phone or portal.',
          'Keep benefit notes tied to the visit.',
        ],
        cta: 'Next',
        preview: <FocusedPreview focus="verification" />,
      },
      {
        icon: PaymentsIcon,
        title: 'Record a confirmed payment',
        description:
          'Collect the applicable fixed co-pay or record a separately confirmed billing amount so it stays tied to the patient and visit.',
        points: [
          'A fixed co-pay supplies the payment suggestion. Coinsurance does not.',
          'Send an invoice if the patient will pay later.',
        ],
        cta: 'Next',
        preview: <FocusedPreview focus="collection" />,
      },
      {
        icon: PlaylistAddCheckIcon,
        title: 'Run eligibility before appointments',
        description:
          'For patients with insurance on file, run checks in one batch before the day starts. The results show who is covered, what benefits are on file, and who needs a benefits follow-up.',
        points: [
          'Run selected visits or the full list.',
          'Follow up where coverage or benefits need attention.',
        ],
        cta: 'Next',
        preview: <FocusedPreview focus="batch" />,
      },
      {
        icon: ReceiptLongIcon,
        title: 'Eligibility checks cost $0.90 each',
        description:
          'JotPsych charges $0.90 for each eligibility check. Usage is billed with your monthly subscription, so the cost is clear on the same invoice.',
        points: [
          'Every check is $0.90.',
          'Eligibility usage appears with your monthly subscription billing.',
        ],
        cta: 'Finish',
        preview: <FocusedPreview focus="cost" />,
      },
    ],
    [],
  );

  const currentStepIndex = Math.min(Math.max(stepIndex, 0), steps.length - 1);
  const activeStep = steps[currentStepIndex];
  const isLastStep = currentStepIndex === steps.length - 1;

  useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    setStepIndex(Math.min(Math.max(initialStep, 0), steps.length - 1));
  }, [initialMode, initialStep, open, steps.length]);

  const startTour = () => {
    setStepIndex(0);
    setMode('tour');
    onStart?.();
  };

  const dismiss = () => {
    onDismiss();
  };

  const advance = () => {
    if (isLastStep) {
      onComplete();
      return;
    }
    setStepIndex(current => current + 1);
  };

  const back = () => setStepIndex(current => Math.max(current - 1, 0));

  return (
    <>
      <Dialog
        open={open && mode === 'invite'}
        onClose={dismiss}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              boxShadow: '0 20px 50px rgba(15, 23, 42, 0.22)',
            },
          },
        }}
      >
        <Stack sx={{ p: 3, gap: 2.25 }}>
          <Stack
            direction="row"
            sx={{ gap: 2, alignItems: 'flex-start', justifyContent: 'space-between' }}
          >
            <Stack spacing={0.75}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: ELIG_TOKENS.midnight }}>
                New in JotBill
              </Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 750, color: ELIG_TOKENS.ink }}>
                Learn the eligibility worklist in 2 minutes?
              </Typography>
            </Stack>
            <IconButton aria-label="Dismiss onboarding" size="small" onClick={dismiss}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Typography sx={{ fontSize: 14, color: ELIG_TOKENS.inkSoft, lineHeight: 1.55 }}>
            See how to prep today&apos;s visits, record a payer call or portal check, and close out
            copays before claims go out.
          </Typography>
          <DialogActions sx={{ p: 0, gap: 1, justifyContent: 'flex-end' }}>
            <Button color="inherit" onClick={dismiss}>
              Not now
            </Button>
            <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={startTour}>
              Show me
            </Button>
          </DialogActions>
        </Stack>
      </Dialog>

      <Dialog
        open={open && mode === 'tour'}
        onClose={dismiss}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: '0 24px 70px rgba(15, 23, 42, 0.24)',
              maxWidth: 840,
            },
          },
        }}
      >
        <TourHeader onClose={dismiss} />
        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 0.95fr' },
              minHeight: { md: 420 },
            }}
          >
            <EducationSlideLikePanel step={activeStep} />

            <Box
              sx={{
                bgcolor: '#FBFCFE',
                p: { xs: 2, md: 3 },
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {activeStep.preview}
            </Box>
          </Box>
        </DialogContent>
        <TourNavigation
          currentStep={currentStepIndex}
          totalSteps={steps.length}
          nextLabel={activeStep.cta}
          onBack={back}
          onNext={advance}
          onSkip={dismiss}
        />
      </Dialog>
    </>
  );
};

const TourHeader = ({ onClose }: { onClose: () => void }) => (
  <Box
    sx={{
      bgcolor: ELIG_TOKENS.midnight,
      color: '#fff',
      px: 3,
      py: 2,
      position: 'relative',
    }}
  >
    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
      <FactCheckIcon sx={{ fontSize: 24 }} />
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        Eligibility Checks
      </Typography>
      <Box
        component="span"
        sx={{
          bgcolor: 'rgba(255,255,255,0.18)',
          borderRadius: 999,
          px: 1,
          py: 0.25,
          fontSize: 11,
          fontWeight: 700,
        }}
      >
        Quick tour
      </Box>
    </Stack>
    <IconButton
      aria-label="Close eligibility checks tour"
      onClick={onClose}
      sx={{
        position: 'absolute',
        right: 8,
        top: 8,
        color: 'inherit',
      }}
    >
      <CloseIcon />
    </IconButton>
  </Box>
);

const EducationSlideLikePanel = ({ step }: { step: TourStep }) => {
  const Icon = step.icon;

  return (
    <Stack
      sx={{
        p: { xs: 3, md: 4.5 },
        gap: 2.5,
        borderRight: { md: '1px solid' },
        borderColor: 'divider',
      }}
    >
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Box
          sx={{
            bgcolor: '#F1EFFB',
            color: ELIG_TOKENS.midnight,
            borderRadius: 3,
            width: 48,
            height: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 24 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              color: ELIG_TOKENS.ink,
              mb: 0.75,
              fontSize: 24,
              fontWeight: 760,
              lineHeight: 1.18,
            }}
          >
            {step.title}
          </Typography>
          <Typography sx={{ color: ELIG_TOKENS.inkSoft, lineHeight: 1.7, fontSize: 15 }}>
            {step.description}
          </Typography>
        </Box>
      </Stack>

      <Stack spacing={1.5}>
        {step.points.map(point => (
          <Stack key={point} direction="row" spacing={1.5} sx={{ alignItems: 'baseline' }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: ELIG_TOKENS.midnight,
                flexShrink: 0,
                position: 'relative',
                top: -1,
              }}
            />
            <Typography sx={{ fontSize: 14, color: ELIG_TOKENS.ink }}>{point}</Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

const TourNavigation = ({
  currentStep,
  totalSteps,
  nextLabel,
  onBack,
  onNext,
  onSkip,
}: {
  currentStep: number;
  totalSteps: number;
  nextLabel: string;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
}) => (
  <Stack
    direction="row"
    sx={{
      px: 3,
      py: 2,
      borderTop: '1px solid',
      borderColor: 'divider',
      alignItems: 'center',
      justifyContent: 'space-between',
    }}
  >
    <Stack direction="row" spacing={0.75}>
      {Array.from({ length: totalSteps }, (_, index) => (
        <Box
          key={index}
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: index === currentStep ? ELIG_TOKENS.midnight : '#AEB4C2',
            transition: 'background-color 0.2s',
          }}
        />
      ))}
    </Stack>

    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
      <Button color="inherit" onClick={onSkip}>
        Skip
      </Button>
      {currentStep > 0 && (
        <Button color="inherit" startIcon={<ArrowBackIcon />} onClick={onBack}>
          Back
        </Button>
      )}
      <Button
        variant="contained"
        endIcon={nextLabel === 'Next' ? <ArrowForwardIcon /> : undefined}
        onClick={onNext}
      >
        {nextLabel}
      </Button>
    </Stack>
  </Stack>
);

const FocusedPreview = ({ focus, framed = true }: { focus: PreviewFocus; framed?: boolean }) => {
  const counts = summarize(MOCK_WORKLIST_ITEMS);
  const collectItem = MOCK_WORKLIST_ITEMS[0];
  const attentionItem = MOCK_WORKLIST_ITEMS[1];
  const verifiedItem = MOCK_WORKLIST_ITEMS[2];
  const runnableItem = MOCK_WORKLIST_ITEMS[3];

  return (
    <Stack
      spacing={2}
      sx={{
        width: '100%',
        maxWidth: framed ? 360 : 'none',
        mx: framed ? 'auto' : 0,
      }}
    >
      {focus === 'overview' && (
        <PreviewShell title="Today's prep">
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <MetricPill
              label="Ready"
              value={counts.good}
              color={ELIG_TOKENS.green}
              bg={ELIG_TOKENS.greenSoft}
            />
            <MetricPill
              label="Needs check"
              value={counts.run}
              color={ELIG_TOKENS.gray}
              bg={ELIG_TOKENS.graySoft}
            />
            <MetricPill
              label="Follow-up"
              value={counts.verify}
              color={ELIG_TOKENS.amber}
              bg={ELIG_TOKENS.amberSoft}
            />
          </Stack>
          <MiniWorklistRow item={attentionItem} />
        </PreviewShell>
      )}

      {focus === 'badges' && (
        <PreviewShell title="Visits needing action">
          <MiniWorklistRow item={collectItem} />
          <StatusLegend />
        </PreviewShell>
      )}

      {focus === 'verification' && <VerificationFlowPreview item={attentionItem} />}

      {focus === 'collection' && <CollectionFlowPreview item={collectItem} />}

      {focus === 'batch' && (
        <PreviewShell title="Run eligibility checks">
          <MiniWorklistRow item={runnableItem} selectable selected />
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Button size="small" variant="outlined" sx={bulkButtonSx}>
              Run selected
            </Button>
            <Button
              size="small"
              variant="contained"
              sx={{
                ...bulkButtonSx,
                bgcolor: ELIG_TOKENS.midnight,
                '&:hover': { bgcolor: ELIG_TOKENS.midnight, filter: 'brightness(0.92)' },
              }}
            >
              Run all (1)
            </Button>
          </Stack>
        </PreviewShell>
      )}

      {focus === 'cost' && <CostDisclosurePreview />}

      {framed && (focus === 'overview' || focus === 'badges') && (
        <MiniWorklistRow item={verifiedItem} quiet />
      )}
    </Stack>
  );
};

const PreviewShell = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Stack
    spacing={1.5}
    sx={{
      bgcolor: '#fff',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      p: 2,
      boxShadow: '0 12px 28px rgba(15, 23, 42, 0.07)',
    }}
  >
    <Typography sx={{ fontSize: 12, fontWeight: 750, color: ELIG_TOKENS.inkSoft }}>
      {title}
    </Typography>
    {children}
  </Stack>
);

const MiniWorklistRow: React.FC<{
  item: WorklistItem;
  selectable?: boolean;
  selected?: boolean;
  quiet?: boolean;
}> = ({ item, selectable = false, selected = false, quiet = false }) => {
  const state = deriveWorklistRowState(item);
  const status = STATUS_UI[state];
  const isRunnable = isRunnableState(state);
  const action =
    state === 'collect'
      ? 'Invoice patient'
      : isRunnable
        ? 'Run check'
        : (item.action_verb ?? status.action);

  return (
    <Stack
      direction="row"
      spacing={1.25}
      sx={{
        bgcolor: quiet ? ELIG_TOKENS.greenTint : status.tint,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.25,
        p: 1.25,
        opacity: quiet ? 0.7 : 1,
        alignItems: 'flex-start',
      }}
    >
      {selectable && <Checkbox size="small" checked={selected} sx={{ p: 0.25, mt: 0.25 }} />}
      <PatientIdentity item={item} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={0.75} sx={{ minWidth: 0, alignItems: 'center' }}>
          <StatusGlyph item={item} state={state} />
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: status.color }} noWrap>
            {item.readiness_label ?? status.label}
          </Typography>
        </Stack>
        <Typography
          sx={{
            fontSize: 12,
            color: ELIG_TOKENS.inkSoft,
            lineHeight: 1.25,
            mt: 0.25,
          }}
        >
          {item.summary}
        </Typography>
        {!quiet && <ActionButton label={action} isPrimary={isRunnable} />}
      </Box>
    </Stack>
  );
};

const StatusLegend = () => (
  <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
    <LegendItem readiness="verified" label="Ready" />
    <LegendItem readiness="collect" label="Collect" />
    <LegendItem readiness="attention" label="Verify" />
  </Stack>
);

const VerificationFlowPreview: React.FC<{ item: WorklistItem }> = ({ item }) => (
  <PreviewShell title="Log payer verification">
    <MiniWorklistRow item={item} quiet />
    <Stack spacing={1.25}>
      <Box>
        <Typography sx={{ fontSize: 12, fontWeight: 650, color: ELIG_TOKENS.ink, mb: 0.5 }}>
          How did you verify?
        </Typography>
        <RadioGroup row defaultValue="phone">
          <FormControlLabel value="phone" control={<Radio size="small" />} label="Phone" />
          <FormControlLabel value="portal" control={<Radio size="small" />} label="Portal" />
        </RadioGroup>
      </Box>
      <Stack direction="row" spacing={1}>
        <TextField
          label="Rep"
          size="small"
          value="Janelle"
          slotProps={{ input: { readOnly: true } }}
          sx={{ flex: 1 }}
        />
        <TextField
          label="Ref #"
          size="small"
          value="Aetna-4921"
          slotProps={{ input: { readOnly: true } }}
          sx={{ flex: 1 }}
        />
      </Stack>
      <TextField
        label="What was confirmed"
        size="small"
        multiline
        minRows={2}
        value="BH outpatient covered. $30 copay."
        slotProps={{ input: { readOnly: true } }}
      />
      <Button variant="contained" size="small" sx={primaryButtonSx}>
        Save verification
      </Button>
    </Stack>
  </PreviewShell>
);

const CollectionFlowPreview: React.FC<{ item: WorklistItem }> = ({ item }) => (
  <PreviewShell title="Record confirmed payment">
    <MiniWorklistRow item={item} quiet />
    <Stack spacing={1.25}>
      <Stack
        direction="row"
        spacing={1}
        sx={{
          bgcolor: ELIG_TOKENS.greenSoft,
          color: ELIG_TOKENS.green,
          borderRadius: 1,
          px: 1.25,
          py: 1,
          alignItems: 'center',
        }}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 760 }}>Collect $30 co-pay</Typography>
      </Stack>
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
        <PaymentMethodPill label="Card on file" active />
        <PaymentMethodPill label="Invoice" />
        <PaymentMethodPill label="Cash/check" />
      </Stack>
      <TextField
        label="Reason"
        size="small"
        value="Co-pay"
        slotProps={{ input: { readOnly: true } }}
      />
      <TextField
        label="Amount"
        size="small"
        value="30.00"
        slotProps={{
          input: {
            readOnly: true,
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          },
        }}
      />
      <Stack direction="row" spacing={1}>
        <Button variant="contained" size="small" sx={primaryButtonSx}>
          Charge card on file
        </Button>
        <Button variant="text" size="small" sx={{ textTransform: 'none' }}>
          Send invoice
        </Button>
      </Stack>
    </Stack>
  </PreviewShell>
);

const CostDisclosurePreview = () => (
  <PreviewShell title="Billing example">
    <Stack spacing={1.5}>
      <Stack
        sx={{
          bgcolor: '#F1EFFB',
          borderRadius: 1.25,
          px: 1.5,
          py: 1.25,
        }}
      >
        <Typography sx={{ fontSize: 28, fontWeight: 780, color: ELIG_TOKENS.midnight }}>
          $0.90
        </Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: ELIG_TOKENS.ink }}>
          per eligibility check
        </Typography>
      </Stack>

      <Stack
        spacing={1}
        sx={{
          border: '1px solid',
          borderColor: ELIG_TOKENS.lineStrong,
          borderRadius: 1.25,
          p: 1.25,
        }}
      >
        <InvoiceLine label="Eligibility checks" value="32 x $0.90" />
        <InvoiceLine label="Usage this month" value="$28.80" strong />
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1 }}>
          <Typography sx={{ fontSize: 12, color: ELIG_TOKENS.inkSoft, lineHeight: 1.35 }}>
            Billed with your JotPsych monthly subscription.
          </Typography>
        </Box>
      </Stack>
    </Stack>
  </PreviewShell>
);

const InvoiceLine = ({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) => (
  <Stack direction="row" sx={{ gap: 1, justifyContent: 'space-between' }}>
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: strong ? 750 : 600,
        color: strong ? ELIG_TOKENS.ink : ELIG_TOKENS.inkSoft,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: 12,
        fontWeight: 750,
        color: strong ? ELIG_TOKENS.ink : ELIG_TOKENS.inkSoft,
      }}
    >
      {value}
    </Typography>
  </Stack>
);

const PaymentMethodPill = ({ label, active = false }: { label: string; active?: boolean }) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: active ? ELIG_TOKENS.midnight : ELIG_TOKENS.lineStrong,
      bgcolor: active ? ELIG_TOKENS.midnight : '#fff',
      color: active ? '#fff' : ELIG_TOKENS.inkSoft,
      borderRadius: 999,
      px: 1,
      py: 0.4,
      fontSize: 12,
      fontWeight: 650,
    }}
  >
    {label}
  </Box>
);

const LegendItem = ({
  readiness,
  label,
}: {
  readiness: 'verified' | 'collect' | 'attention';
  label: string;
}) => (
  <Stack
    direction="row"
    spacing={0.75}
    sx={{
      bgcolor: ELIG_TOKENS.bg,
      borderRadius: 999,
      px: 1,
      py: 0.5,
      alignItems: 'center',
    }}
  >
    <ReadinessIndicator readiness={readiness} size={9} />
    <Typography sx={{ fontSize: 12, fontWeight: 650, color: ELIG_TOKENS.inkSoft }}>
      {label}
    </Typography>
  </Stack>
);

const PatientIdentity: React.FC<{ item: WorklistItem }> = ({ item }) => {
  const { firstName, lastName } = splitDisplayName(item.patient_name);

  return (
    <Stack direction="row" spacing={0.75} sx={{ minWidth: 0, alignItems: 'flex-start' }}>
      <Avatar
        id={item.patient_id}
        firstName={firstName}
        lastName={lastName}
        sx={{
          width: 28,
          height: 28,
          fontWeight: 650,
          flexShrink: 0,
          transform: `translateY(${TOP_ALIGNED_BADGE_PIXEL_OFFSET}px)`,
        }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 650, color: ELIG_TOKENS.ink }} noWrap>
          {item.patient_name}
        </Typography>
        <Typography sx={{ fontSize: 12, color: ELIG_TOKENS.inkSoft, lineHeight: 1.15 }} noWrap>
          {item.payer_name ?? 'No payer'}
        </Typography>
      </Box>
    </Stack>
  );
};

const StatusGlyph: React.FC<{ item: WorklistItem; state: WorklistRowState }> = ({
  item,
  state,
}) => {
  if (item.readiness) return <ReadinessIndicator readiness={item.readiness} size={10} />;
  if (state === 'self_pay') return <NeutralDot color={ELIG_TOKENS.green} />;
  return <NeutralDot color={ELIG_TOKENS.gray} />;
};

const NeutralDot: React.FC<{ color: string }> = ({ color }) => (
  <Box
    sx={{
      width: 10,
      height: 10,
      borderRadius: '50%',
      border: `2px solid ${color}`,
      flexShrink: 0,
    }}
  />
);

const ActionButton: React.FC<{
  label: string;
  isPrimary?: boolean;
}> = ({ label, isPrimary = false }) => (
  <Button
    size="small"
    variant={isPrimary ? 'contained' : 'text'}
    sx={{
      minWidth: 0,
      textTransform: 'none',
      fontSize: 12,
      fontWeight: 650,
      whiteSpace: 'nowrap',
      justifyContent: 'flex-start',
      px: isPrimary ? 1.25 : 0,
      py: isPrimary ? 0.55 : 0,
      ...(isPrimary
        ? {
            bgcolor: ELIG_TOKENS.midnight,
            '&:hover': { bgcolor: ELIG_TOKENS.midnight, filter: 'brightness(0.92)' },
          }
        : {
            color: ELIG_TOKENS.midnight,
            '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
          }),
    }}
  >
    {`${label} ->`}
  </Button>
);

const MetricPill: React.FC<{
  label: string;
  value: number;
  color: string;
  bg: string;
}> = ({ label, value, color, bg }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.75,
      bgcolor: bg,
      color,
      borderRadius: 999,
      px: 1.25,
      py: 0.5,
      fontSize: 12,
      fontWeight: 650,
    }}
  >
    <Box component="span">{value}</Box>
    <Box component="span">{label}</Box>
  </Box>
);

const bulkButtonSx = {
  minWidth: 0,
  textTransform: 'none',
  fontSize: 12,
  fontWeight: 650,
  px: 1.25,
  py: 0.55,
  borderColor: ELIG_TOKENS.lineStrong,
};

const primaryButtonSx = {
  alignSelf: 'flex-start',
  textTransform: 'none',
  fontSize: 12,
  fontWeight: 650,
  bgcolor: ELIG_TOKENS.midnight,
  '&:hover': { bgcolor: ELIG_TOKENS.midnight, filter: 'brightness(0.92)' },
};

function summarize(items: WorklistItem[]) {
  return items.reduce(
    (acc, item) => {
      const state = deriveWorklistRowState(item);
      if (state === 'verified_lock_active' || state === 'collected' || state === 'self_pay') {
        acc.good += 1;
      } else if (state === 'awaiting_check') {
        acc.run += 1;
      } else if (
        state === 'attention' ||
        state === 'verified_lock_expired' ||
        state === 'needs_coverage' ||
        state === 'collect' ||
        state === 'info'
      ) {
        acc.verify += 1;
      } else if (state === 'blocked') {
        acc.review += 1;
      }
      return acc;
    },
    { good: 0, run: 0, verify: 0, review: 0 },
  );
}

function splitDisplayName(name: string): { firstName: string; lastName: string } {
  const [firstName = '', ...rest] = name.trim().split(/\s+/);
  return { firstName, lastName: rest.join(' ') };
}
